import ChatIcon from "@/assets/icons/ChatIcon";
import EmbeddingIcon from "@/assets/icons/EmbeddingIcon";
import ImageGenerationIcon from "@/assets/icons/ImageGenerationIcon";
import MultimodalIcon from "@/assets/icons/MultimodalIcon";
import SpeechToTextIcon from "@/assets/icons/SpeechToTextIcon";
import TextToSpeechIcon from "@/assets/icons/TextToSpeechIcon";
import type {
  CatalogSelectOption,
  ModelFieldConfig,
  ModelFilterType,
  ModelSourceFilter,
  ModelTypeConfig,
  TranslateFn,
} from "../types";
import { STT_RESPONSE_FORMATS } from "../utils/catalog";

export const getSourceOptions = (
  t: TranslateFn,
): CatalogSelectOption<ModelSourceFilter>[] => [
  { value: "bunyan", label: t("aiCatalog.bunyan") },
  { value: "shared", label: t("aiCatalog.shared") },
  { value: "private", label: t("aiCatalog.private") },
];

export const getTypeOptions = (
  t: TranslateFn,
): CatalogSelectOption<ModelFilterType>[] => [
  { value: "chat", label: t("aiCatalog.chat") },
  { value: "multimodal", label: t("deployments.multimodal") },
  { value: "tts", label: t("aiCatalog.textToSpeech") },
  { value: "stt", label: t("aiCatalog.speechToText") },
  { value: "embedding", label: t("deployments.embedding") },
  { value: "image_generation", label: t("aiCatalog.imageGeneration") },
];

export const getModelTypes = (t: TranslateFn): ModelTypeConfig[] => [
  {
    key: "is_chat_model",
    label: t("aiCatalog.chat"),
    desc: t("aiCatalog.chatDesc"),
    icon: ChatIcon,
  },
  {
    key: "is_multimodal",
    label: t("deployments.multimodal"),
    desc: t("deployments.multimodalDesc"),
    icon: MultimodalIcon,
  },
  {
    key: "is_embedding_model",
    label: t("deployments.embedding"),
    desc: t("deployments.embeddingDesc"),
    icon: EmbeddingIcon,
  },
  {
    key: "is_tts_model",
    label: t("aiCatalog.textToSpeech"),
    desc: t("aiCatalog.textToSpeechDesc"),
    icon: TextToSpeechIcon,
  },
  {
    key: "is_stt_model",
    label: t("aiCatalog.speechToText"),
    desc: t("aiCatalog.speechToTextDesc"),
    icon: SpeechToTextIcon,
  },
  {
    key: "is_image_generation_model",
    label: t("aiCatalog.imageGeneration"),
    desc: t("aiCatalog.imageGenerationDesc"),
    icon: ImageGenerationIcon,
  },
];

export const getModelFieldConfig = (
  t: TranslateFn,
): Record<string, ModelFieldConfig[]> => {
  const baseGenerationFields: ModelFieldConfig[] = [
    {
      type: "text",
      key: "context_length",
      label: t("modelRegistration.contextLength"),
      placeholder: t("modelRegistration.contextLength"),
      inputType: "number",
      inputProps: { min: 1, step: 1 },
      grid: { xs: 6, md: 4 },
    },
    {
      type: "text",
      key: "max_output_tokens",
      label: t("modelRegistration.maxOutputTokens"),
      placeholder: t("modelRegistration.maxOutputTokens"),
      inputType: "number",
      inputProps: { min: 0, step: 1 },
      grid: { xs: 6, md: 4 },
    },
    {
      type: "text",
      key: "temperature",
      label: t("modelRegistration.temperature"),
      placeholder: t("modelRegistration.temperatureHelper"),
      inputType: "number",
      inputProps: { min: 0, max: 1, step: 0.01 },
      grid: { xs: 6, md: 4 },
    },
    {
      type: "text",
      key: "top_p",
      label: t("modelRegistration.topP"),
      placeholder: t("modelRegistration.topP"),
      inputType: "number",
      inputProps: { min: 0, max: 1, step: 0.01 },
      grid: { xs: 6, md: 4 },
    },
    {
      type: "text",
      key: "max_tokens",
      label: t("modelRegistration.maxTokens"),
      placeholder: t("modelRegistration.maxTokensHelper"),
      helperText: t("modelRegistration.maxTokensHelper"),
      inputType: "number",
      inputProps: { min: 0, step: 1 },
      grid: { xs: 6, md: 4 },
    },
  ];

  return {
    is_chat_model: baseGenerationFields,
    is_multimodal: [
      ...baseGenerationFields.slice(0, 2),
      {
        type: "text",
        key: "max_image_size",
        label: t("modelRegistration.maxImageSize"),
        placeholder: t("modelRegistration.maxImageSize"),
        inputType: "number",
        inputProps: { min: 0.1, max: 20, step: 0.1 },
        grid: { xs: 6, md: 4 },
        suffix: "MB",
      },
      {
        type: "text",
        key: "max_images_per_request",
        label: t("modelRegistration.maxImagesPerRequest"),
        placeholder: t("modelRegistration.maxImagesPerRequest"),
        inputType: "number",
        inputProps: { min: 1, step: 1 },
        grid: { xs: 6, md: 4 },
      },
      ...baseGenerationFields.slice(2),
    ],
    is_embedding_model: [
      {
        type: "text",
        key: "embedding_dimension",
        label: t("modelRegistration.embeddingDimension"),
        placeholder: t("modelRegistration.embeddingDimensionHelper"),
        inputType: "number",
        inputProps: { min: 1, step: 1 },
        grid: { xs: 12, md: 6 },
      },
      {
        type: "text",
        key: "max_input_tokens",
        label: t("modelRegistration.maxInputTokens"),
        placeholder: t("modelRegistration.maxInputTokens"),
        inputType: "number",
        inputProps: { min: 1, step: 1 },
        grid: { xs: 12, md: 6 },
      },
    ],
    is_tts_model: [
      {
        type: "text",
        key: "max_input_characters",
        label: t("modelRegistration.maxInputCharacters"),
        placeholder: t("modelRegistration.maxInputCharacters"),
        inputType: "number",
        inputProps: { min: 1, step: 1 },
        grid: { xs: 6, md: 4 },
      },
    ],
    is_stt_model: [
      {
        type: "text",
        key: "max_audio_duration_seconds",
        label: t("modelRegistration.maxAudioDurationSeconds"),
        placeholder: t("modelRegistration.maxAudioDurationSeconds"),
        inputType: "number",
        inputProps: { min: 1, step: 1 },
        grid: { xs: 6, md: 6 },
      },
      {
        type: "text",
        key: "max_audio_size",
        label: t("modelRegistration.maxAudioSize"),
        placeholder: t("modelRegistration.maxAudioSize"),
        inputType: "number",
        inputProps: { min: 0.1, max: 20, step: 0.1 },
        grid: { xs: 6, md: 6 },
        suffix: "MB",
      },
      {
        type: "select",
        key: "supported_audio_format",
        label: t("modelRegistration.responseFormat"),
        placeholder: t("modelRegistration.responseFormat"),
        helperText: t("modelRegistration.sttResponseFormatHelper"),
        grid: { xs: 12, md: 12 },
        options: STT_RESPONSE_FORMATS.map((format) => ({
          label: format,
          value: format,
        })),
        allowEmpty: true,
      },
    ],
    is_image_generation_model: [
      {
        type: "chip",
        key: "output_formats",
        label: t("modelRegistration.outputFormats"),
        placeholder: "png, jpeg, webp, etc...",
        grid: { xs: 12, md: 12 },
      },
    ],
  };
};
